import { useState } from "react";
import { Avatar, Button, Form, Input, List, Modal, Popover, Tabs, Typography } from "antd";
import { KeyOutlined, LogoutOutlined, QuestionCircleOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { selectUser, signOut } from "@/app/authSlice";
import { users } from "@/api";
import { useAction } from "@/hooks";

const { Text } = Typography;

/** Initials stand in for an avatar image, as in the original header. */
const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase())
    .filter(Boolean)
    .slice(0, 2)
    .join("");

/**
 * The header's profile control, after BMR_Frontend's
 * HeaderContent/Profile: the avatar and the user's name sit inline in the
 * header, and clicking opens a card with who is signed in, a logout button
 * in the corner, and the Profile tab's action list beneath.
 *
 * The reference shows an email under the name; this app's session carries
 * `role` rather than an address, so the role sits there instead.
 */
export default function ProfileMenu() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    setOpen(false);
    dispatch(signOut());
  };

  const [changingPw, setChangingPw] = useState(false);
  const [pwForm] = Form.useForm();
  const [changePw, changingBusy] = useAction(
    async () => {
      const v = await pwForm.validateFields();
      // The fresh token pair replaces the stored one inside the client —
      // the change revokes everything issued before it, this tab included.
      await users.changeOwnPassword(v.current, v.next);
      setChangingPw(false);
      pwForm.resetFields();
    },
    { success: "Password changed — other sessions are signed out" },
  );

  const menuItems = [
    {
      key: "password",
      icon: <KeyOutlined style={{ minWidth: 24, color: "var(--text-secondary)" }} />,
      text: "Change password",
      onClick: () => {
        setOpen(false);
        setChangingPw(true);
      },
    },
    {
      key: "help",
      icon: (
        <QuestionCircleOutlined
          style={{ minWidth: 24, color: "var(--text-secondary)" }}
        />
      ),
      text: "Help & documentation",
      onClick: () => {
        setOpen(false);
        navigate("/guide");
      },
    },
    {
      key: "logout",
      icon: <LogoutOutlined style={{ minWidth: 24, color: "var(--text-secondary)" }} />,
      text: "Logout",
      onClick: handleLogout,
    },
  ];

  const content = (
    <div style={{ width: 290 }}>
      <div className="flex items-center justify-between gap-3 px-1 pb-3">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar size={40} style={{ background: "var(--accent)" }}>
            {initials(user?.name) || <UserOutlined />}
          </Avatar>
          <div className="min-w-0 leading-tight">
            <div className="truncate text-sm font-semibold">{user?.name}</div>
            <Text type="secondary" className="truncate text-xs">
              {user?.role}
            </Text>
          </div>
        </div>
        <Button
          type="text"
          icon={<LogoutOutlined />}
          onClick={handleLogout}
          aria-label="Sign out"
          style={{ color: "var(--text-secondary)" }}
        />
      </div>

      <div style={{ borderTop: "1px solid var(--border)" }}>
        <Tabs
          size="small"
          items={[
            {
              key: "profile",
              label: (
                <span className="flex items-center gap-2">
                  <UserOutlined />
                  <span>Profile</span>
                </span>
              ),
              children: (
                <List
                  size="small"
                  dataSource={menuItems}
                  renderItem={(item) => (
                    <List.Item
                      key={item.key}
                      onClick={item.onClick}
                      className="cursor-pointer px-0"
                      style={{ borderBottom: "none" }}
                    >
                      <div className="flex w-full items-center gap-3 rounded p-1">
                        {item.icon}
                        <span className="text-sm">{item.text}</span>
                      </div>
                    </List.Item>
                  )}
                />
              ),
            },
          ]}
        />
      </div>
    </div>
  );

  return (
    <>
    <Modal
      title="Change your password"
      open={changingPw}
      onOk={changePw}
      onCancel={() => {
        setChangingPw(false);
        pwForm.resetFields();
      }}
      okText="Change password"
      confirmLoading={changingBusy}
      destroyOnHidden
    >
      <Text type="secondary" className="mb-3 block text-xs">
        Changing your password signs out every other session and device —
        the change is also the revocation. This tab stays signed in.
      </Text>
      <Form form={pwForm} layout="vertical" requiredMark={false}>
        <Form.Item
          name="current"
          label="Current password"
          rules={[{ required: true, message: "Your current password" }]}
        >
          <Input.Password autoComplete="current-password" />
        </Form.Item>
        <Form.Item
          name="next"
          label="New password"
          rules={[
            { required: true, message: "At least 8 characters" },
            { min: 8, message: "At least 8 characters" },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="confirm"
          label="Confirm new password"
          dependencies={["next"]}
          rules={[
            { required: true, message: "Repeat the new password" },
            ({ getFieldValue }) => ({
              validator: (_, v) =>
                !v || v === getFieldValue("next")
                  ? Promise.resolve()
                  : Promise.reject(new Error("Passwords do not match")),
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      overlayStyle={{ paddingTop: 9 }}
    >
      <button
        type="button"
        className="profile-trigger flex shrink-0 cursor-pointer items-center gap-2 rounded border-0 p-1"
        style={{ background: open ? "var(--icon-btn-bg-hover)" : "transparent" }}
        aria-label="Account"
      >
        <Avatar size={32} style={{ background: "var(--accent)", fontSize: 12 }}>
          {initials(user?.name) || <UserOutlined />}
        </Avatar>
        {/* The name rides alongside the avatar, as in Profile/index.js —
            hidden on narrow screens where the header runs out of room. */}
        <span className="hidden max-w-[140px] truncate text-sm font-medium md:inline">
          {user?.name}
        </span>
      </button>
    </Popover>
    </>
  );
}